// Server-Sent Events parser para POST com Bearer auth.
// EventSource nativo só faz GET sem headers customizados; aqui usamos fetch+ReadableStream.

import { activeUrl, activeToken } from '../store/global';
import type { AnyAgnoEvent } from '../types/agno-events';

export interface RunInput {
  targetId: string;
  isTeam: boolean;
  message: string;
  sessionId?: string;
  userId?: string;
  files?: File[];
  extra?: Record<string, string>;
  signal?: AbortSignal;
}

export interface RunCallbacks {
  onEvent: (event: AnyAgnoEvent) => void;
  onError?: (err: Error) => void;
  onComplete?: () => void;
}

export async function streamRun(input: RunInput, cb: RunCallbacks): Promise<void> {
  const { targetId, isTeam, message, sessionId, userId, files, extra, signal } = input;

  const path = isTeam ? `/teams/${targetId}/runs` : `/agents/${targetId}/runs`;
  const form = new FormData();
  form.append('message', message);
  form.append('stream', 'true');
  if (sessionId) form.append('session_id', sessionId);
  if (userId) form.append('user_id', userId);
  if (extra) for (const [k, v] of Object.entries(extra)) form.append(k, v);
  if (files) for (const f of files) form.append('files', f);

  const tok = activeToken.value;
  const headers: HeadersInit = { Accept: 'text/event-stream' };
  if (tok) (headers as Record<string, string>).Authorization = `Bearer ${tok}`;

  const res = await fetch(`${activeUrl.value}${path}`, {
    method: 'POST',
    headers,
    body: form,
    signal,
  });

  if (!res.ok) {
    const body = await res.text();
    cb.onError?.(new Error(`HTTP ${res.status}: ${body.slice(0, 300)}`));
    return;
  }

  const reader = res.body?.getReader();
  if (!reader) {
    cb.onError?.(new Error('Response body is empty'));
    return;
  }

  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let nlnlIdx: number;
      while ((nlnlIdx = buffer.indexOf('\n\n')) !== -1) {
        const block = buffer.slice(0, nlnlIdx);
        buffer = buffer.slice(nlnlIdx + 2);
        const parsed = parseSseBlock(block);
        if (parsed) cb.onEvent(parsed);
      }
    }
    if (buffer.trim()) {
      const parsed = parseSseBlock(buffer);
      if (parsed) cb.onEvent(parsed);
    }
    cb.onComplete?.();
  } catch (err) {
    if ((err as Error).name === 'AbortError') {
      cb.onComplete?.();
    } else {
      cb.onError?.(err as Error);
    }
  }
}

function parseSseBlock(block: string): AnyAgnoEvent | null {
  let eventName: string | undefined;
  const dataLines: string[] = [];

  for (const line of block.split('\n')) {
    if (!line) continue;
    if (line.startsWith(':')) continue;
    const colon = line.indexOf(':');
    if (colon < 0) continue;
    const field = line.slice(0, colon).trim();
    let val = line.slice(colon + 1);
    if (val.startsWith(' ')) val = val.slice(1);
    if (field === 'event') eventName = val;
    else if (field === 'data') dataLines.push(val);
  }

  if (!dataLines.length) return null;
  const raw = dataLines.join('\n');
  try {
    const data = JSON.parse(raw) as AnyAgnoEvent;
    if (eventName && !data.event) (data as { event: string }).event = eventName;
    return data;
  } catch {
    return null;
  }
}
